import { v4 } from 'uuid';

enum DeviceType {
  DESKTOP = 'DESKTOP',
  MOBILE = 'MOBILE',
  TABLET = 'TABLET'
}

interface UserInfo {
  id?: string;
  deviceType?: DeviceType;
  ip?: string;
  userAgent?: string;
  browser?: string;
  os?: string;
  device?: string;
  language?: string;
  timezone?: string;
  customParams?: Record<string, any>;
  customId?: string;
}

class User {
  private _userInfo: UserInfo = {};

  constructor() {
    const userInfo = localStorage.getItem('getdemo_user');
    if (userInfo) {
      this._userInfo = { ...this._userInfo, ...JSON.parse(userInfo) };
    } else {
      this._userInfo.id = v4();
    }
  }

  public get userInfo(): UserInfo {
    return { ...this._userInfo };
  }

  public async collectUserInfo(): Promise<void> {
    this._userInfo = {
      id: this.getId(),
      customId: this.getCustomId(),
      deviceType: this.detectDeviceType(),
      userAgent: navigator.userAgent,
      browser: this.detectBrowser(),
      os: this.detectOS(),
      device: this.getDeviceInfo(),
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      customParams: this.getCustomParams()
    };

    await this.getIPAddress();
  }

  public getId(): string {
    return this._userInfo.id || '';
  }

  public setCustomId(id: string): void {
    this._userInfo.customId = id;
  }

  public setCustomParams(params: Record<string, any>): void {
    this._userInfo.customParams = params;
  }

  public getCustomId(): string | undefined {
    return this._userInfo.customId;
  }

  public getCustomParams(): Record<string, any> {
    return this._userInfo.customParams || {};
  }

  public identifyUser = (id?: string, params?: Record<string, any>) => {
    const existingParams = this._userInfo.customParams || {};
    const mergedParams = { ...existingParams, ...params };

    this.setCustomParams(mergedParams);
    if (id) {
      this.setCustomId(id);
    }

    localStorage.setItem('getdemo_user', JSON.stringify(this.getInfo()));
  };

  private detectDeviceType(): DeviceType {
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile = /mobile|android|iphone|ipad|phone/i.test(userAgent);
    const isTablet = /tablet|ipad/i.test(userAgent);

    if (isTablet) return DeviceType.TABLET;
    if (isMobile) return DeviceType.MOBILE;
    return DeviceType.DESKTOP;
  }

  private detectBrowser(): string {
    const userAgent = navigator.userAgent;
    let browserName = 'Unknown';
    let browserVersion = 'Unknown';

    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
      browserName = 'Chrome';
      browserVersion = userAgent.match(/Chrome\/(\d+)/)?.[1] || 'Unknown';
    } else if (userAgent.includes('Firefox')) {
      browserName = 'Firefox';
      browserVersion = userAgent.match(/Firefox\/(\d+)/)?.[1] || 'Unknown';
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      browserName = 'Safari';
      browserVersion = userAgent.match(/Version\/(\d+)/)?.[1] || 'Unknown';
    } else if (userAgent.includes('Edg')) {
      browserName = 'Edge';
      browserVersion = userAgent.match(/Edg\/(\d+)/)?.[1] || 'Unknown';
    } else if (userAgent.includes('MSIE') || userAgent.includes('Trident/')) {
      browserName = 'Internet Explorer';
      browserVersion = userAgent.match(/(?:MSIE |rv:)(\d+)/)?.[1] || 'Unknown';
    }

    return `${browserName} ${browserVersion}`;
  }

  private detectOS(): string {
    const userAgent = navigator.userAgent;
    let osName = 'Unknown';
    let osVersion = 'Unknown';

    if (userAgent.includes('Windows')) {
      osName = 'Windows';
      if (userAgent.includes('Windows NT 10.0')) osVersion = '10';
      else if (userAgent.includes('Windows NT 6.3')) osVersion = '8.1';
      else if (userAgent.includes('Windows NT 6.2')) osVersion = '8';
      else if (userAgent.includes('Windows NT 6.1')) osVersion = '7';
    } else if (userAgent.includes('Mac OS X')) {
      osName = 'macOS';
      osVersion = userAgent.match(/Mac OS X (\d+_\d+)/)?.[1]?.replace('_', '.') || 'Unknown';
    } else if (userAgent.includes('Linux')) {
      osName = 'Linux';
    } else if (userAgent.includes('Android')) {
      osName = 'Android';
      osVersion = userAgent.match(/Android (\d+)/)?.[1] || 'Unknown';
    } else if (userAgent.includes('iOS')) {
      osName = 'iOS';
      osVersion = userAgent.match(/OS (\d+_\d+)/)?.[1]?.replace('_', '.') || 'Unknown';
    }

    return `${osName} ${osVersion}`;
  }

  private getDeviceInfo(): string {
    const deviceType = this.detectDeviceType();
    return deviceType;
  }

  private async getIPAddress(): Promise<void> {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      this._userInfo.ip = data.ip;
    } catch (error) {
      console.warn('Could not get IP address:', error);
    }
  }

  public getInfo(): UserInfo {
    return this.userInfo;
  }

  public isMobile(): boolean {
    return this._userInfo.device === DeviceType.MOBILE;
  }

  public isTablet(): boolean {
    return this._userInfo.device === DeviceType.TABLET;
  }

  public isDesktop(): boolean {
    return this._userInfo.device === DeviceType.DESKTOP;
  }

  public getBrowser(): string | undefined {
    return this._userInfo.browser;
  }

  public getOS(): string | undefined {
    return this._userInfo.os;
  }

  public getLanguage(): string | undefined {
    return this._userInfo.language;
  }

  public getTimezone(): string | undefined {
    return this._userInfo.timezone;
  }

  public getIP(): string | undefined {
    return this._userInfo.ip;
  }
}

export default User;
export { DeviceType };
export type { UserInfo };
